import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core"
import { SubdomainService } from "src/app/core/_base/subdomain.service";
import { MadAuthService } from "src/app/core/mad-auth/mad-auth.service";
import { HotelComparableAssetClass } from "./hotel-comparable";
import { environment } from "src/environments/environment";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AssetClassLandAreaModel } from "src/app/core/asset_class";
import { AssetClassSizeModel, AssetClassTenure } from "src/app/core/comparable";
import { EsgIndicator } from "src/app/core/v2/state/esg-indicator/type";
import { AdministrativeArea, Locality, Street } from "../../../location";

@Injectable({
    providedIn: 'root'
})
export class HotelComparableAPIService {
    constructor(
        private http: HttpClient,
        private authService: MadAuthService,
        private subdomainService: SubdomainService
    ) { }

    fetchList(): Observable<{ data: HotelComparableAssetClass[] }> {
        return this.http.get<{ data: HotelComparableAPIResource[] }>(
            environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/comparables/hotels`,
            {
                headers: this.authService.getAuthHeaders()
            }
        ).pipe(
            map(response => {
                return {
                    data: response.data.map(mapAPIResourceToModel)
                }
            })
        )
    }

    fetch(id: number): Observable<HotelComparableAssetClass> {
        return this.http.get<{ data: HotelComparableAPIResource }>(
            environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/comparables/hotels/${id}`,
            {
                headers: this.authService.getAuthHeaders()
            }
        ).pipe(
            map(response => mapAPIResourceToModel(response.data))
        )
    }

    store(assetClass: HotelComparableAssetClass, fileList: any[]) {
        return this.http.post<any>(
            environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/comparables/hotels`,
            {
                payload: assetClass,
                fileList
            },
            {
                headers: this.authService.getAuthHeaders()
            }
        )
    }

    update(assetClass: HotelComparableAssetClass, fileList: any[]) {
        return this.http.put<unknown>(
            environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/comparables/hotels/${assetClass.id}`,
            {
                payload: assetClass,
                fileList
            },
            {
                headers: this.authService.getAuthHeaders()
            }
        )
    }
}

interface HotelComparableAPIResource {
    id: number
    ref_num: string
    status: number
    form_type: number

    country_id: number
    city_id: number
    zip_code: string,
    latitude: string
    longitude: string
    location_grade_id: number
    address: string
    location_surroundings: string,
    unit: string | null,
    building_name: string | null,
    streets: Street[],
    localities: Locality[],
    administrative_areas: AdministrativeArea[]

    add_info_on_land: boolean,
    land_parcel_name: string,
    coordinate_reference_system_id: number,
    boundary_type_id: number,
    land_area: AssetClassLandAreaModel,
    points: any[],
    land_area_components: any[],

    property_type_id: number,
    hotel_rating_id: number,
    hotel_apartment_rating_id: number,
    completion_status: string,
    estimated_age: number
    estimated_remaining_lifespan_from: number,
    estimated_remaining_lifespan_to: number,
    developer: string,
    number_of_stories: string,
    views: string,
    plot_id: string,
    plot_area: number,
    makani_no: string,
    dewa_no: string,
    municipality_no: string,
    state_of_repair_desc: string,
    construction_desc: string,
    upgrades_condition_id: number,
    number_of_units: string,
    floor_area_measurement_comment: string,
    room_schedule_type: any
    construction_categories: any[]
    upgrades_conditions: any[]
    room_schedules: any[]
    floor_area_measurements: any[]
    accommodation_file_name: string,
    accommodation_file_path: string,
    room_schedule_file_name: string,
    room_schedule_file_path: string,
    outlet_file_name: string,
    outlet_file_path: string,

    serviceAndInfraData: any
    externalAspectData: any
    internalAspectData: any
    grounds: any
    sizes: AssetClassSizeModel[]
    esgIndicator: EsgIndicator
    tenures: AssetClassTenure[],
    buildings: any[]

    ac_pictures: any[]
    picture: string

    size: string,
    used_times: number,
    can_be_deleted: boolean,
    userDeletedBy: string
    deletedBy: string
    createdBy: string
    lastUpdatedBy: string
    created_at: string
    updated_at: string
}

function mapAPIResourceToModel(resource: HotelComparableAPIResource): HotelComparableAssetClass {
    const ac = new HotelComparableAssetClass()

    ac.status = resource.status
    ac.form_type = resource.form_type

    ac.id = resource.id
    ac.ref_num = resource.ref_num

    ac.location_data = {
        country_id: resource.country_id,
        city_id: resource.city_id,
        // address: resource.address,
        latitude: Number(resource.latitude),
        longitude: Number(resource.longitude),
        // location_grade_id: resource.location_grade_id,
        location_surrounding: resource.location_surroundings,
        // boundary_type_id: resource.boundary_type_id,
        // city_of_location: null,
        // time_offset: null,
        zip_code: resource.zip_code,
        unit: resource.unit,
        building_name: resource.building_name,
        streets: resource.streets,
        localities: resource.localities,
        administrative_areas: resource.administrative_areas
        // community_id: null,
        // distance_to_the_sea: null,
        // elevation_from_sea: null

    }
    ac.add_info_on_land = resource.add_info_on_land
    ac.land_parcel_name = resource.land_parcel_name
    ac.coordinate_reference_system_id = resource.coordinate_reference_system_id
    ac.boundary_type_id = resource.boundary_type_id
    if (resource.land_area) {
        ac.landArea = resource.land_area
    }
    ac.points = resource.points
    ac.landAreaComponents = resource.land_area_components

    ac.property_type_id = resource.property_type_id
    ac.hotel_rating_id = resource.hotel_rating_id
    ac.hotel_apartment_rating_id = resource.hotel_apartment_rating_id
    ac.completion_status = resource.completion_status
    ac.estimated_age = resource.estimated_age
    ac.estimated_remaining_lifespan_from = resource.estimated_remaining_lifespan_from
    ac.estimated_remaining_lifespan_to = resource.estimated_remaining_lifespan_to
    ac.developer = resource.developer
    ac.number_of_stories = resource.number_of_stories
    ac.views = resource.views
    ac.plot_id = resource.plot_id
    ac.plot_area = resource.plot_area
    ac.makani_no = resource.makani_no
    ac.dewa_no = resource.dewa_no
    ac.municipality_no = resource.municipality_no
    ac.state_of_repair_desc = resource.state_of_repair_desc
    ac.construction_desc = resource.construction_desc
    ac.upgrades_condition_id = resource.upgrades_condition_id
    ac.number_of_units = resource.number_of_units
    ac.floor_area_measurement_comment = resource.floor_area_measurement_comment
    ac.construction_categories = resource.construction_categories
    ac.upgrades_conditions = resource.upgrades_conditions
    ac.room_schedules = resource.room_schedules
    ac.room_schedule_type = resource.room_schedule_type
    ac.floor_area_measurements = resource.floor_area_measurements
    ac.hotel_accommodation_file = resource.accommodation_file_name
        ? { name: resource.accommodation_file_name, path: resource.accommodation_file_path}
        : null
    ac.room_schedule_file = resource.room_schedule_file_name
        ? { name: resource.room_schedule_file_name, path: resource.room_schedule_file_path}
        : null
    ac.outlet_file = resource.outlet_file_name
        ? {name: resource.outlet_file_name, path: resource.outlet_file_path}
        : null

    ac.service_and_infra = resource.serviceAndInfraData
    ac.external_aspect = resource.externalAspectData
    ac.internal_aspect = resource.internalAspectData
    ac.esg_indicator = resource.esgIndicator
    ac.grounds = resource.grounds
    ac.sizes = resource.sizes
    ac.tenures = resource.tenures

    ac.size = resource.size
    ac.can_be_deleted = resource.can_be_deleted
    ac.userDeletedBy = resource.userDeletedBy
    ac.deletedBy = resource.deletedBy
    ac.createdBy = resource.createdBy
    ac.lastUpdatedBy = resource.lastUpdatedBy
    ac.created_at = resource.created_at
    ac.updated_at = resource.updated_at
    ac.used_times = resource.used_times

    ac.ac_pictures = resource.ac_pictures
    ac.picture = resource.picture

    return ac
}

